import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSubscription } from '@/features/subscription/hooks/useSubscription'
import { useInvoices } from '@/features/subscription/hooks/useInvoices'
import { useSubscriptionMutations } from '@/features/subscription/hooks/useSubscriptionMutations'
import { subscriptionApi } from '@/features/subscription/api/subscription.api'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import type { Subscription, Invoice, Plan } from '@/features/subscription/types'
import { useTranslation } from 'react-i18next'

const STATUS_CLASSES: Record<Subscription['status'], string> = {
  trial: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  past_due: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-surface-100 text-neutral-600',
  expired: 'bg-red-100 text-red-800',
}

const INVOICE_STATUS_CLASSES: Record<Invoice['status'], string> = {
  paid: 'bg-green-100 text-green-800',
  open: 'bg-blue-100 text-blue-800',
  draft: 'bg-surface-100 text-neutral-600',
  uncollectible: 'bg-red-100 text-red-800',
  void: 'bg-surface-100 text-neutral-600',
}

function formatDate(dateStr: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(dateStr))
}

function formatAmount(cents: number, currency: string, locale: string) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100)
}

function PageSkeleton() {
  return (
    <div className="mx-auto max-w-2xl animate-pulse px-4 py-10">
      {[160, 120, 96, 200].map((h, i) => (
        <div key={i} className={`mb-4 h-${h === 160 ? 40 : h === 120 ? 32 : h === 96 ? 24 : 48} rounded-xl bg-surface-100`} />
      ))}
    </div>
  )
}

export function SubscriptionManagePage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const navigate = useNavigate()
  const { data: subscription, isLoading: subLoading } = useSubscription()
  const { data: invoices = [], isLoading: invLoading } = useInvoices()
  const { cancelSubscription, changePlan } = useSubscriptionMutations()
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [portalLoading, setPortalLoading] = useState(false)

  if (subLoading) return <PageSkeleton />

  if (!subscription) {
    return (
      <main className="min-h-screen bg-surface-50">
        <div className="mx-auto max-w-2xl px-4 py-10 text-center">
          <p className="mb-4 text-neutral-600">{t('subscription.none')}</p>
          <Link
            to="/subscription"
            className="inline-flex min-h-[44px] items-center rounded-md bg-primary-500 px-6 text-sm font-semibold text-white hover:bg-primary-600"
          >
            {t('subscription.see_plans')}
          </Link>
        </div>
      </main>
    )
  }

  const handlePortal = async () => {
    try {
      setPortalLoading(true)
      const res = await subscriptionApi.fetchPortalUrl()
      window.location.href = res.data.data.url
    } catch {
      setPortalLoading(false)
    }
  }

  const handleChangePlan = (plan: Plan) => {
    changePlan.mutate(plan)
  }

  const handleCancelConfirmed = async () => {
    await cancelSubscription.mutateAsync()
    setConfirmCancel(false)
    navigate('/subscription')
  }

  const changePlanError = changePlan.error as Error | null
  const cancelError = cancelSubscription.error as Error | null

  return (
    <main className="min-h-screen bg-surface-50">
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <h1 className="mb-6 text-2xl font-bold text-neutral-900">{t('subscription.manage_title')}</h1>

        {/* Trial notice */}
        {subscription.status === 'trial' && (
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-4">
            <p className="text-sm font-semibold text-blue-900">
              {t('subscription.trial_banner', { count: subscription.days_remaining_in_trial })}
            </p>
            <p className="mt-1 text-sm text-blue-700">{t('subscription.trial_hint')}</p>
            <Link
              to="/subscription"
              className="mt-3 inline-flex min-h-[44px] items-center rounded-md bg-primary-500 px-4 text-sm font-semibold text-white hover:bg-primary-600"
            >
              {t('subscription.subscribe_now')}
            </Link>
          </div>
        )}

        {/* Past due notice */}
        {subscription.status === 'past_due' && (
          <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-4">
            <p className="text-sm font-semibold text-amber-900">{t('subscription.past_due_title')}</p>
            <p className="mt-1 text-sm text-amber-700">{t('subscription.past_due_hint')}</p>
            <button
              onClick={handlePortal}
              disabled={portalLoading}
              className="mt-3 inline-flex min-h-[44px] items-center rounded-md bg-amber-500 px-4 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
            >
              {portalLoading ? t('common.loading') : t('subscription.update_card')}
            </button>
          </div>
        )}

        {/* Current subscription */}
        <section className="mb-6 rounded-xl border border-surface-200 bg-white p-5">
          <h2 className="mb-4 text-base font-semibold text-neutral-900">{t('subscription.current_title')}</h2>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-neutral-600">{t('subscription.plan')}</dt>
              <dd className="mt-0.5 font-medium text-neutral-900 capitalize">
                {subscription.plan === 'monthly' ? t('subscription.monthly') : t('subscription.annual')}
              </dd>
            </div>
            <div>
              <dt className="text-neutral-600">{t('subscription.status')}</dt>
              <dd className="mt-0.5">
                <span
                  className={[
                    'inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold',
                    STATUS_CLASSES[subscription.status],
                  ].join(' ')}
                >
                  {t(`subscription.statuses.${subscription.status}`)}
                </span>
              </dd>
            </div>
            {subscription.current_period_end && (
              <div>
                <dt className="text-neutral-600">{t('subscription.next_payment')}</dt>
                <dd className="mt-0.5 font-medium text-neutral-900">
                  {formatDate(subscription.current_period_end, locale)}
                </dd>
              </div>
            )}
            {subscription.status === 'trial' && subscription.trial_ends_at && (
              <div>
                <dt className="text-neutral-600">{t('subscription.trial_end')}</dt>
                <dd className="mt-0.5 font-medium text-neutral-900">
                  {formatDate(subscription.trial_ends_at, locale)}
                </dd>
              </div>
            )}
          </dl>
        </section>

        {/* Change plan */}
        {(subscription.status === 'active') && (
          <section className="mb-6 rounded-xl border border-surface-200 bg-white p-5">
            <h2 className="mb-3 text-base font-semibold text-neutral-900">{t('subscription.change_plan_title')}</h2>
            {changePlanError && (
              <Alert variant="error" className="mb-3">
                {changePlanError.message ?? t('subscription.change_plan_error')}
              </Alert>
            )}
            {subscription.plan === 'monthly' ? (
              <div className="flex items-center justify-between">
                <p className="text-sm text-neutral-700">{t('subscription.switch_to_annual_hint')}</p>
                <Button
                  variant="secondary"
                  isLoading={changePlan.isPending}
                  onClick={() => handleChangePlan('annual')}
                  className="min-h-[44px]"
                >
                  {t('subscription.switch_to_annual')}
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-neutral-700">{t('subscription.switch_to_monthly_hint')}</p>
                <Button
                  variant="secondary"
                  isLoading={changePlan.isPending}
                  onClick={() => handleChangePlan('monthly')}
                  className="min-h-[44px]"
                >
                  {t('subscription.switch_to_monthly')}
                </Button>
              </div>
            )}
          </section>
        )}

        {/* Cancel subscription */}
        {(subscription.status === 'active' || subscription.status === 'trial') && (
          <section className="mb-6 rounded-xl border border-surface-200 bg-white p-5">
            <h2 className="mb-3 text-base font-semibold text-neutral-900">
              {t('subscription.cancel_title')}
            </h2>
            {!confirmCancel ? (
              <button
                onClick={() => setConfirmCancel(true)}
                className="min-h-[44px] rounded-md border border-red-300 px-4 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
              >
                {t('subscription.cancel_action')}
              </button>
            ) : (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="mb-4 text-sm font-medium text-red-900">
                  {t('subscription.cancel_confirm')}
                </p>
                {cancelError && (
                  <Alert variant="error" className="mb-3">
                    {cancelError.message ?? t('subscription.cancel_error')}
                  </Alert>
                )}
                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => setConfirmCancel(false)}
                    className="min-h-[44px]"
                  >
                    {t('common.cancel')}
                  </Button>
                  <button
                    onClick={handleCancelConfirmed}
                    disabled={cancelSubscription.isPending}
                    className="min-h-[44px] rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                  >
                    {cancelSubscription.isPending ? t('subscription.cancelling') : t('subscription.cancel_confirmed')}
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Invoices */}
        <section className="rounded-xl border border-surface-200 bg-white p-5">
          <h2 className="mb-4 text-base font-semibold text-neutral-900">{t('subscription.invoices_title')}</h2>
          {invLoading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 rounded bg-surface-100" />
              ))}
            </div>
          ) : invoices.length === 0 ? (
            <p className="text-sm text-neutral-600">{t('subscription.invoices_empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-200 text-left">
                    <th className="pb-2 font-medium text-neutral-600">{t('subscription.invoice_date')}</th>
                    <th className="pb-2 font-medium text-neutral-600">{t('subscription.invoice_amount')}</th>
                    <th className="pb-2 font-medium text-neutral-600">{t('subscription.invoice_status')}</th>
                    <th className="pb-2 font-medium text-neutral-600">{t('subscription.invoice_link')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-200">
                  {invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td className="py-3 text-neutral-700">{formatDate(inv.created_at, locale)}</td>
                      <td className="py-3 font-medium text-neutral-900">
                        {formatAmount(inv.amount_cents, inv.currency, locale)}
                      </td>
                      <td className="py-3">
                        <span
                          className={[
                            'inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold',
                            INVOICE_STATUS_CLASSES[inv.status],
                          ].join(' ')}
                        >
                          {t(`subscription.invoice_statuses.${inv.status}`)}
                        </span>
                      </td>
                      <td className="py-3">
                        {inv.invoice_url ? (
                          <a
                            href={inv.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-500 hover:underline"
                          >
                            {t('subscription.download')}
                          </a>
                        ) : (
                          <span className="text-neutral-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </main>
  )
}
